import { useMemo, useState, type ComponentType, type ReactNode } from "react";
import {
  GraduationCap,
  Trophy,
  RotateCcw,
  CheckCircle2,
  XCircle,
  BarChart3,
  Brain,
  ArrowRight,
  Sparkles,
  Target,
  ShieldCheck,
  BookOpen,
} from "lucide-react";
import {
  quizSections,
  getQuestionsForSection,
  getQuizSectionById,
  type QuizSection,
} from "../data/quizQuestions";
import { QuizCategory, QuizQuestion } from "../types/quiz";

type AnswersMap = Record<string, number>;
type SkillLevel = "Beginner" | "Intermediate" | "Expert";

const STORAGE_KEY = "dtf_wizard_skill_test_best_score";

function getLevel(score: number): SkillLevel {
  if (score >= 81) return "Expert";
  if (score >= 51) return "Intermediate";
  return "Beginner";
}

function getLevelStyles(level: SkillLevel) {
  if (level === "Expert") {
    return "border-emerald-400/25 bg-emerald-500/10 text-emerald-300";
  }
  if (level === "Intermediate") {
    return "border-dtf-blue/30 bg-dtf-blue/10 text-dtf-blue";
  }
  return "border-amber-400/25 bg-amber-500/10 text-amber-300";
}

function getCategoryRecommendation(category: QuizCategory, percent: number) {
  if (percent >= 80) return "Strong area. Keep sharpening it.";
  if (percent >= 55) return "Decent foundation, but there is room to improve.";

  switch (category) {
    case "Artwork Prep":
      return "Spend more time on file prep, edge cleanup, and white layer setup.";
    case "Printing":
      return "Focus on nozzle checks, consistency, and machine behavior.";
    case "Powder & Curing":
      return "Work on even powder coverage and proper curing judgment.";
    case "Pressing":
      return "Review time, temperature, pressure, and final press technique.";
    case "Troubleshooting":
      return "Practice isolating one variable at a time and checking the full workflow.";
    default:
      return "This area needs more review.";
  }
}

function BluePanel({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={[
        "relative overflow-hidden rounded-[28px] border border-dtf-accent/20",
        "bg-[radial-gradient(circle_at_top_center,rgba(37,99,235,0.18),transparent_42%),linear-gradient(180deg,rgba(7,16,35,0.98)_0%,rgba(6,14,30,0.98)_100%)]",
        "shadow-[0_0_0_1px_rgba(245,158,11,0.05),0_0_34px_rgba(37,99,235,0.16)]",
        className,
      ].join(" ")}
    >
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,rgba(37,99,235,0.08),transparent_30%,transparent_70%,rgba(37,99,235,0.06))]" />
      <div className="pointer-events-none absolute inset-0 rounded-[28px] ring-1 ring-inset ring-white/5" />
      {children}
    </div>
  );
}

function StatPill({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-2xl border border-white/10 bg-black/20 px-4 py-3">
      <div className="mb-1 flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-dtf-text-muted">
        <span className="text-dtf-accent">{icon}</span>
        <span>{label}</span>
      </div>
      <div className="text-base font-semibold text-white">{value}</div>
    </div>
  );
}

function SectionHeading({
  icon: Icon,
  eyebrow,
  title,
  description,
}: {
  icon: ComponentType<{ className?: string }>;
  eyebrow: string;
  title: string;
  description?: string;
}) {
  return (
    <div className="mb-6">
      <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-dtf-blue/25 bg-dtf-blue/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.22em] text-dtf-blue">
        <Icon className="h-4 w-4" />
        {eyebrow}
      </div>

      <h2 className="text-2xl font-extrabold tracking-tight text-white md:text-3xl">
        {title}
      </h2>

      {description ? (
        <p className="mt-3 max-w-3xl text-sm leading-6 text-dtf-text-muted md:text-base">
          {description}
        </p>
      ) : null}
    </div>
  );
}

function FeatureCard({
  icon: Icon,
  title,
  text,
}: {
  icon: ComponentType<{ className?: string }>;
  title: string;
  text: string;
}) {
  return (
    <div className="rounded-[24px] border border-white/10 bg-black/20 p-5 shadow-[0_0_18px_rgba(37,99,235,0.08)]">
      <div className="flex items-start gap-4">
        <div className="rounded-2xl border border-dtf-blue/20 bg-dtf-blue/10 p-3 shadow-[0_0_12px_rgba(59,130,246,0.18)]">
          <Icon className="h-5 w-5 text-dtf-blue" />
        </div>

        <div>
          <h3 className="text-lg font-bold text-white">{title}</h3>
          <p className="mt-2 text-sm leading-6 text-dtf-text-muted">{text}</p>
        </div>
      </div>
    </div>
  );
}

function MetricCard({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-[24px] border border-white/10 bg-black/20 p-5 shadow-[0_0_18px_rgba(37,99,235,0.08)]">
      <p className="text-sm text-dtf-text-muted">{label}</p>
      <p className="mt-2 text-3xl font-black text-white">{value}</p>
    </div>
  );
}

function ReviewBlock({
  title,
  tone,
  children,
}: {
  title: string;
  tone: "bad" | "good" | "info";
  children: ReactNode;
}) {
  const classes =
    tone === "bad"
      ? "border-red-400/20 bg-red-500/10 text-red-300"
      : tone === "good"
      ? "border-emerald-400/20 bg-emerald-500/10 text-emerald-300"
      : "border-dtf-blue/20 bg-dtf-blue/10 text-dtf-blue";

  return (
    <div className={`rounded-2xl border p-4 ${classes}`}>
      <p className="text-xs font-semibold uppercase tracking-[0.16em]">{title}</p>
      <div className="mt-2 text-sm leading-6 text-white/85">{children}</div>
    </div>
  );
}

function QuizSectionCard({
  section,
  onStart,
}: {
  section: QuizSection;
  onStart: (sectionId: string) => void;
}) {
  return (
    <BluePanel className="h-full">
      <div className="flex h-full flex-col p-5">
        <div className="inline-flex w-fit items-center gap-2 rounded-full border border-dtf-blue/20 bg-dtf-blue/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-dtf-blue">
          <BookOpen className="h-4 w-4" />
          Module
        </div>

        <h3 className="mt-4 text-xl font-bold text-white">{section.title}</h3>
        <p className="mt-3 text-sm leading-6 text-dtf-text-muted">
          {section.description}
        </p>

        <div className="mt-5 rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-white">
          <span className="font-bold">{section.questions.length}</span> questions
        </div>

        <div className="mt-6">
          <button
            onClick={() => onStart(section.id)}
            className="inline-flex items-center justify-center gap-2 rounded-2xl border border-dtf-blue/35 bg-dtf-blue/12 px-5 py-3 text-sm font-semibold text-white transition hover:-translate-y-0.5 hover:border-dtf-blue/60 hover:bg-dtf-blue/18"
          >
            Start Module
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </BluePanel>
  );
}

export default function KnowledgeTest() {
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);
  const [started, setStarted] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<AnswersMap>({});

  const selectedSection = useMemo(
    () => (selectedSectionId ? getQuizSectionById(selectedSectionId) : undefined),
    [selectedSectionId]
  );

  const activeQuestions = useMemo<QuizQuestion[]>(
    () => (selectedSectionId ? getQuestionsForSection(selectedSectionId) : []),
    [selectedSectionId]
  );

  const totalSections = quizSections.length;
  const totalQuestionsAllSections = quizSections.reduce(
    (sum, section) => sum + section.questions.length,
    0
  );

  const totalQuestions = activeQuestions.length;
  const totalCategories = new Set(activeQuestions.map((q) => q.category)).size;
  const currentQuestion: QuizQuestion | undefined = activeQuestions[currentIndex];
  const currentAnswer = currentQuestion ? answers[currentQuestion.id] : undefined;

  const bestScore = useMemo(() => {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? Number(raw) : null;
  }, [submitted]);

  const answeredCount = Object.keys(answers).length;
  const progressPercent =
    totalQuestions > 0 ? Math.round((answeredCount / totalQuestions) * 100) : 0;

  const results = useMemo(() => {
    if (!selectedSection || activeQuestions.length === 0) {
      return {
        correct: 0,
        score: 0,
        level: "Beginner" as SkillLevel,
        categoryStats: [] as Array<{
          category: QuizCategory;
          total: number;
          correct: number;
          percent: number;
          recommendation: string;
        }>,
      };
    }

    const correct = activeQuestions.filter(
      (q) => answers[q.id] === q.correctAnswer
    ).length;

    const score =
      activeQuestions.length > 0
        ? Math.round((correct / activeQuestions.length) * 100)
        : 0;

    const level = getLevel(score);

    const categoryStats = Array.from(
      new Set(activeQuestions.map((q) => q.category))
    ).map((category) => {
      const questions = activeQuestions.filter((q) => q.category === category);
      const correctCount = questions.filter(
        (q) => answers[q.id] === q.correctAnswer
      ).length;
      const percent =
        questions.length > 0
          ? Math.round((correctCount / questions.length) * 100)
          : 0;

      return {
        category: category as QuizCategory,
        total: questions.length,
        correct: correctCount,
        percent,
        recommendation: getCategoryRecommendation(category as QuizCategory, percent),
      };
    });

    return {
      correct,
      score,
      level,
      categoryStats,
    };
  }, [answers, activeQuestions, selectedSection]);

  const missedQuestions = useMemo(() => {
    return activeQuestions.filter((q) => answers[q.id] !== q.correctAnswer);
  }, [answers, activeQuestions]);

  const handleSelect = (questionId: string, optionIndex: number) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: optionIndex,
    }));
  };

  const handleChooseSection = (sectionId: string) => {
    setSelectedSectionId(sectionId);
    setStarted(false);
    setSubmitted(false);
    setCurrentIndex(0);
    setAnswers({});
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleStartSection = () => {
    if (!selectedSectionId) return;
    setStarted(true);
    setSubmitted(false);
    setCurrentIndex(0);
    setAnswers({});
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleNext = () => {
    if (currentIndex < totalQuestions - 1) {
      setCurrentIndex((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
    }
  };

  const handleSubmit = () => {
    const correct = activeQuestions.filter(
      (q) => answers[q.id] === q.correctAnswer
    ).length;
    const score =
      activeQuestions.length > 0
        ? Math.round((correct / activeQuestions.length) * 100)
        : 0;

    const existingBest = localStorage.getItem(STORAGE_KEY);
    const best = existingBest ? Number(existingBest) : 0;

    if (score > best) {
      localStorage.setItem(STORAGE_KEY, String(score));
    }

    setSubmitted(true);
    setStarted(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleRestart = () => {
    setStarted(false);
    setSubmitted(false);
    setCurrentIndex(0);
    setAnswers({});
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBackToModules = () => {
    setSelectedSectionId(null);
    setStarted(false);
    setSubmitted(false);
    setCurrentIndex(0);
    setAnswers({});
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (!selectedSectionId) {
    return (
      <div className="min-h-screen bg-[#070b14] text-white">
        <div className="mx-auto max-w-6xl px-4 py-8 md:px-6 md:py-10">
          <BluePanel className="p-6 md:p-10">
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_center,rgba(59,130,246,0.24),transparent_45%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.10),transparent_28%)]" />

            <div className="relative grid gap-8 lg:grid-cols-[1.15fr_0.85fr] lg:items-center">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-dtf-blue/25 bg-dtf-blue/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.24em] text-dtf-blue">
                  <GraduationCap className="h-4 w-4" />
                  Skill Check Modules
                </div>

                <h1 className="mt-5 text-4xl font-black tracking-tight text-white md:text-5xl">
                  DTF Knowledge Training
                </h1>

                <p className="mt-4 max-w-2xl text-sm leading-7 text-dtf-text md:text-base">
                  Pick a section and train one skill area at a time instead of
                  grinding through one giant quiz.
                </p>

                <div className="mt-6 flex flex-wrap gap-3">
                  <div className="rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-white">
                    <span className="font-bold">{totalSections}</span> modules
                  </div>
                  <div className="rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-white">
                    <span className="font-bold">{totalQuestionsAllSections}</span>{" "}
                    total questions
                  </div>
                  <div className="rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-white">
                    Train one section at a time
                  </div>
                </div>

                {bestScore !== null && !Number.isNaN(bestScore) && (
                  <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-emerald-400/20 bg-emerald-500/10 px-4 py-2 text-sm font-semibold text-emerald-300">
                    <Trophy className="h-4 w-4" />
                    Best saved score: {bestScore}%
                  </div>
                )}
              </div>

              <div className="grid gap-4">
                <FeatureCard
                  icon={Brain}
                  title="Section-based training"
                  text="Focus on one part of DTF at a time instead of treating everything like one giant test."
                />
                <FeatureCard
                  icon={BarChart3}
                  title="Cleaner learning path"
                  text="Let users choose Artwork Prep, Printing, Powder & Curing, Pressing, or Troubleshooting."
                />
                <FeatureCard
                  icon={Sparkles}
                  title="Easier to expand later"
                  text="This structure makes it much easier to add more questions to each section over time."
                />
              </div>
            </div>
          </BluePanel>

          <div className="mt-8">
            <SectionHeading
              icon={BookOpen}
              eyebrow="Modules"
              title="Choose a section"
              description="Each module runs as its own quiz so users can train one area without being overwhelmed."
            />

            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {quizSections.map((section) => (
                <QuizSectionCard
                  key={section.id}
                  section={section}
                  onStart={handleChooseSection}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!started && !submitted && selectedSection) {
    return (
      <div className="min-h-screen bg-[#070b14] text-white">
        <div className="mx-auto max-w-6xl px-4 py-8 md:px-6 md:py-10">
          <BluePanel className="p-6 md:p-10">
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_center,rgba(59,130,246,0.24),transparent_45%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.10),transparent_28%)]" />

            <div className="relative grid gap-8 lg:grid-cols-[1.15fr_0.85fr] lg:items-center">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-dtf-blue/25 bg-dtf-blue/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.24em] text-dtf-blue">
                  <GraduationCap className="h-4 w-4" />
                  {selectedSection.title}
                </div>

                <h1 className="mt-5 text-4xl font-black tracking-tight text-white md:text-5xl">
                  {selectedSection.title} Module
                </h1>

                <p className="mt-4 max-w-2xl text-sm leading-7 text-dtf-text md:text-base">
                  {selectedSection.description}
                </p>

                <div className="mt-6 flex flex-wrap gap-3">
                  <div className="rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-white">
                    <span className="font-bold">{totalQuestions}</span> questions
                  </div>
                  <div className="rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-white">
                    <span className="font-bold">{totalCategories}</span> skill categories
                  </div>
                  <div className="rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-white">
                    Module-only score
                  </div>
                </div>

                <div className="mt-8 flex flex-wrap gap-3">
                  <button
                    onClick={handleStartSection}
                    className="inline-flex items-center justify-center gap-2 rounded-2xl border border-dtf-blue/35 bg-dtf-blue/12 px-6 py-3 text-sm font-semibold text-white transition hover:-translate-y-0.5 hover:border-dtf-blue/60 hover:bg-dtf-blue/18"
                  >
                    Start Module
                    <ArrowRight className="h-4 w-4" />
                  </button>

                  <button
                    onClick={handleBackToModules}
                    className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/10 bg-black/20 px-6 py-3 text-sm font-semibold text-white transition hover:bg-black/30"
                  >
                    Back to Modules
                  </button>
                </div>
              </div>

              <div className="grid gap-4">
                <FeatureCard
                  icon={Target}
                  title="Focused training"
                  text="Users can improve one weak area without wading through unrelated questions."
                />
                <FeatureCard
                  icon={ShieldCheck}
                  title="Cleaner score"
                  text="The result reflects this section only, which makes the feedback more useful."
                />
                <FeatureCard
                  icon={Brain}
                  title="Easier expansion"
                  text="You can keep adding more questions to this section later without reworking the whole system."
                />
              </div>
            </div>
          </BluePanel>
        </div>
      </div>
    );
  }

  if (submitted && selectedSection) {
    return (
      <div className="min-h-screen bg-[#070b14] text-white">
        <div className="mx-auto max-w-6xl px-4 py-8 md:px-6 md:py-10">
          <BluePanel className="p-6 md:p-10">
            <div className="flex flex-wrap items-center gap-3">
              <div className="inline-flex items-center gap-2 rounded-full border border-dtf-blue/25 bg-dtf-blue/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.24em] text-dtf-blue">
                <Trophy className="h-4 w-4" />
                {selectedSection.title} Results
              </div>

              <div
                className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold ${getLevelStyles(
                  results.level
                )}`}
              >
                {results.level}
              </div>
            </div>

            <h1 className="mt-5 text-4xl font-black tracking-tight text-white md:text-5xl">
              Your {selectedSection.title} Score: {results.score}%
            </h1>

            <p className="mt-4 max-w-2xl text-sm leading-7 text-dtf-text md:text-base">
              You answered {results.correct} out of {totalQuestions} correctly in{" "}
              <span className="font-bold text-white">{selectedSection.title}</span>.
              Your current level for this module is{" "}
              <span className="font-bold text-white">{results.level}</span>.
            </p>

            <div className="mt-8 grid gap-4 md:grid-cols-3">
              <MetricCard label="Score" value={`${results.score}%`} />
              <MetricCard
                label="Correct Answers"
                value={`${results.correct} / ${totalQuestions}`}
              />
              <MetricCard label="Level" value={results.level} />
            </div>
          </BluePanel>

          <div className="mt-8">
            <SectionHeading
              icon={BarChart3}
              eyebrow="Breakdown"
              title="Category Breakdown"
              description="See how you performed inside this module."
            />

            <div className="grid gap-4 md:grid-cols-2">
              {results.categoryStats.map((item) => (
                <BluePanel key={item.category}>
                  <div className="p-5">
                    <div className="flex items-center justify-between gap-4">
                      <h3 className="text-lg font-bold text-white">{item.category}</h3>
                      <div className="rounded-full border border-dtf-blue/20 bg-dtf-blue/10 px-3 py-1 text-sm font-semibold text-dtf-blue">
                        {item.percent}%
                      </div>
                    </div>

                    <div className="mt-4 h-3 overflow-hidden rounded-full bg-black/30">
                      <div
                        className="h-full rounded-full bg-[linear-gradient(90deg,rgba(59,130,246,0.95)_0%,rgba(245,158,11,0.95)_100%)]"
                        style={{ width: `${item.percent}%` }}
                      />
                    </div>

                    <p className="mt-4 text-sm text-dtf-text">
                      {item.correct} correct out of {item.total}
                    </p>

                    <p className="mt-3 text-sm leading-6 text-dtf-text-muted">
                      {item.recommendation}
                    </p>
                  </div>
                </BluePanel>
              ))}
            </div>
          </div>

          <div className="mt-8">
            <SectionHeading
              icon={Brain}
              eyebrow="Review"
              title="Missed Questions Review"
              description="Use this section to learn why an answer missed and what the correct thinking should be."
            />

            <div className="grid gap-4">
              {missedQuestions.length === 0 ? (
                <BluePanel>
                  <div className="p-6">
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="mt-0.5 h-5 w-5 text-emerald-300" />
                      <div>
                        <h3 className="text-lg font-bold text-white">Perfect score</h3>
                        <p className="mt-2 text-sm leading-6 text-dtf-text">
                          You did not miss any questions in this module. That is strong work.
                        </p>
                      </div>
                    </div>
                  </div>
                </BluePanel>
              ) : (
                missedQuestions.map((question) => {
                  const selected = answers[question.id];

                  return (
                    <BluePanel key={question.id}>
                      <div className="p-5">
                        <div className="flex flex-wrap items-center gap-3">
                          <div className="rounded-full border border-dtf-blue/20 bg-dtf-blue/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-dtf-blue">
                            {question.category}
                          </div>

                          <div className="rounded-full border border-red-400/20 bg-red-500/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-red-300">
                            Missed
                          </div>
                        </div>

                        <h3 className="mt-4 text-lg font-bold text-white">
                          {question.question}
                        </h3>

                        <div className="mt-4 grid gap-3">
                          <ReviewBlock title="Your answer" tone="bad">
                            {typeof selected === "number"
                              ? question.options[selected]
                              : "No answer selected"}
                          </ReviewBlock>

                          <ReviewBlock title="Correct answer" tone="good">
                            {question.options[question.correctAnswer]}
                          </ReviewBlock>

                          <ReviewBlock title="Explanation" tone="info">
                            {question.explanation}
                          </ReviewBlock>
                        </div>
                      </div>
                    </BluePanel>
                  );
                })
              )}
            </div>
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <button
              onClick={handleRestart}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-dtf-blue/35 bg-dtf-blue/12 px-5 py-3 text-sm font-semibold text-white transition hover:-translate-y-0.5 hover:border-dtf-blue/60 hover:bg-dtf-blue/18"
            >
              <RotateCcw className="h-4 w-4" />
              Retake Module
            </button>

            <button
              onClick={handleBackToModules}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/10 bg-black/20 px-5 py-3 text-sm font-semibold text-white transition hover:bg-black/30"
            >
              Back to Modules
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!currentQuestion || !selectedSection) return null;

  return (
    <div className="min-h-screen bg-[#070b14] text-white">
      <div className="mx-auto max-w-5xl px-4 py-8 md:px-6 md:py-10">
        <BluePanel className="p-6 md:p-8">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="inline-flex items-center gap-2 rounded-full border border-dtf-blue/25 bg-dtf-blue/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.24em] text-dtf-blue">
              <GraduationCap className="h-4 w-4" />
              {selectedSection.title}
            </div>

            <div className="rounded-full border border-white/10 bg-black/20 px-4 py-2 text-sm text-white">
              Question {currentIndex + 1} of {totalQuestions}
            </div>
          </div>

          <div className="mt-6">
            <div className="mb-3 flex items-center justify-between gap-3">
              <span className="rounded-full border border-dtf-accent/20 bg-dtf-accent/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-dtf-accent">
                {currentQuestion.category}
              </span>

              <span className="text-sm text-dtf-text">{progressPercent}% answered</span>
            </div>

            <div className="h-3 overflow-hidden rounded-full bg-black/30">
              <div
                className="h-full rounded-full bg-[linear-gradient(90deg,rgba(59,130,246,0.95)_0%,rgba(245,158,11,0.95)_100%)] transition-all duration-300"
                style={{ width: `${Math.max(progressPercent, 4)}%` }}
              />
            </div>
          </div>

          <div className="mt-8 rounded-[24px] border border-white/10 bg-black/20 p-5 shadow-[0_0_18px_rgba(37,99,235,0.08)] md:p-6">
            <h1 className="text-2xl font-extrabold tracking-tight text-white md:text-3xl">
              {currentQuestion.question}
            </h1>

            <div className="mt-6 grid gap-3">
              {currentQuestion.options.map((option, index) => {
                const selected = currentAnswer === index;

                return (
                  <button
                    key={option}
                    onClick={() => handleSelect(currentQuestion.id, index)}
                    className={`flex items-start gap-3 rounded-2xl border p-4 text-left transition ${
                      selected
                        ? "border-dtf-blue/35 bg-dtf-blue/12 text-white shadow-[0_0_18px_rgba(59,130,246,0.14)]"
                        : "border-white/10 bg-black/10 text-white/85 hover:border-dtf-blue/25 hover:bg-black/20"
                    }`}
                  >
                    <div
                      className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-xs font-bold ${
                        selected
                          ? "border-dtf-blue/40 bg-dtf-blue/18 text-white"
                          : "border-white/15 bg-white/5 text-white/60"
                      }`}
                    >
                      {String.fromCharCode(65 + index)}
                    </div>

                    <span className="text-sm leading-6">{option}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
            <button
              onClick={handleBack}
              disabled={currentIndex === 0}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/10 bg-black/20 px-5 py-3 text-sm font-semibold text-white transition hover:bg-black/30 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Back
            </button>

            <div className="flex flex-wrap gap-3">
              {currentIndex < totalQuestions - 1 ? (
                <button
                  onClick={handleNext}
                  disabled={typeof currentAnswer !== "number"}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl border border-dtf-blue/35 bg-dtf-blue/12 px-5 py-3 text-sm font-semibold text-white transition hover:-translate-y-0.5 hover:border-dtf-blue/60 hover:bg-dtf-blue/18 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Next Question
                  <ArrowRight className="h-4 w-4" />
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={answeredCount !== totalQuestions}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl border border-emerald-400/35 bg-emerald-500/20 px-5 py-3 text-sm font-semibold text-emerald-100 transition hover:-translate-y-0.5 hover:border-emerald-300/60 hover:bg-emerald-500/30 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Submit Module
                  <CheckCircle2 className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {answeredCount !== totalQuestions && currentIndex === totalQuestions - 1 && (
            <div className="mt-5 rounded-2xl border border-amber-400/15 bg-amber-500/10 p-4">
              <div className="flex items-start gap-3">
                <XCircle className="mt-0.5 h-5 w-5 text-amber-300" />
                <p className="text-sm leading-6 text-white/78">
                  You need to answer all {totalQuestions} questions before submitting.
                </p>
              </div>
            </div>
          )}

          <div className="mt-6 grid gap-3 sm:grid-cols-3">
            <StatPill
              icon={<Target className="h-4 w-4" />}
              label="Answered"
              value={`${answeredCount} / ${totalQuestions}`}
            />
            <StatPill
              icon={<BarChart3 className="h-4 w-4" />}
              label="Progress"
              value={`${progressPercent}%`}
            />
            <StatPill
              icon={<ShieldCheck className="h-4 w-4" />}
              label="Level Goal"
              value="Expert"
            />
          </div>

          <div className="mt-5">
            <button
              onClick={handleBackToModules}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/10 bg-black/20 px-5 py-3 text-sm font-semibold text-white transition hover:bg-black/30"
            >
              Back to Modules
            </button>
          </div>
        </BluePanel>
      </div>
    </div>
  );
}